import { useMemo, useState } from "react";
import { KpiCard, Badge } from "@/components/kit";
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import { filterData, kpisOf, dailySeries, mean, fmtN, fmtPct, fmtInt, covLabel, IV_METRICS, C, SITE_COLORS } from "./ivUtils";

const card = "mk-card p-5";

const Spark = ({ pts, color = C.cyan }) => {
  if (!pts || pts.length < 2) return <div className="h-[26px]" />;
  const vs = pts.map((p) => p.v);
  const min = Math.min(...vs), max = Math.max(...vs);
  const norm = (v) => (max === min ? 13 : 24 - ((v - min) / (max - min)) * 22);
  const step = 100 / (pts.length - 1);
  return (
    <svg viewBox="0 0 100 26" className="h-[26px] w-full" preserveAspectRatio="none">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts.map((p, i) => `${i * step},${norm(p.v)}`).join(" ")} />
    </svg>
  );
};

const wkShort = (label) => { const m = label.match(/Week\s*(\d+)/i); return m ? `W${m[1]}` : label.slice(0, 8); };
const SLA_PREF = ["Offered Volume", "Answered Volume", "AHT", "Shrinkage", "VOC", "PMT", "Average Calls / Agent", "Answer Rate", "Abandoned %", "SL"];

export default function IvExecView({ data, slices, kpis, range }) {
  const [segment, setSegment] = useState("Onshore");
  const t = data.targets;
  const covMap = { voc: data.coverage.voc, surveys: data.coverage.voc, answered: data.coverage.calls, heldPct: data.coverage.calls, hold: data.coverage.calls, talk: data.coverage.calls, aht: data.coverage.aht, pmt: data.coverage.pmt, crit: data.coverage.pmt, qsl: data.coverage.qsl };
  const colors = { voc: C.green, surveys: C.blue, answered: C.cyan, heldPct: C.yellow, hold: C.purple, talk: C.purple, aht: C.blue, pmt: C.purple, crit: C.red, qsl: C.yellow };

  const seriesFor = useMemo(() => ({
    voc: dailySeries(slices.voc, (rs) => { const n = rs.reduce((a, r) => a + r.n, 0); return n ? (100 * rs.reduce((a, r) => a + r.t2b, 0)) / n : null; }),
    surveys: dailySeries(slices.voc, (rs) => rs.reduce((a, r) => a + r.n, 0)),
    answered: dailySeries(slices.answered, (rs) => rs.reduce((a, r) => a + r.v, 0)),
    heldPct: dailySeries(slices.held, (rs) => rs.reduce((a, r) => a + r.v, 0)),
    hold: dailySeries(slices.hold, (rs) => mean(rs.map((r) => r.v))),
    talk: dailySeries(slices.talk, (rs) => mean(rs.map((r) => r.v))),
    aht: dailySeries(slices.aht, (rs) => mean(rs.map((r) => r.v))),
    pmt: dailySeries(slices.pmt.filter((p) => p.d), (rs) => mean(rs.map((r) => r.score).filter((v) => v != null))),
    crit: dailySeries(slices.pmt.filter((p) => p.d), (rs) => rs.filter((r) => r.rating === "Critical").length),
    qsl: dailySeries(slices.qsl, (rs) => mean(rs.map((r) => r.score))),
  }), [slices]);

  const sitePair = useMemo(() => {
    const q = kpisOf(filterData(data, { ...range, site: "Qatar" }));
    const i = kpisOf(filterData(data, { ...range, site: "India" }));
    return IV_METRICS(t).map((m) => ({ label: m.label, q: q[m.k], i: i[m.k], fmt: m.pct ? fmtPct : m.int ? fmtInt : (v) => fmtN(v, m.d ?? 1) }));
  }, [data, range, t]);

  const slaKpis = useMemo(() => data.sla.kpis.filter((k) => k.target != null), [data]);
  const segWeeks = useMemo(() => data.sla.weeks.filter((w) => w.segments[segment] && Object.keys(w.segments[segment]).length), [data, segment]);
  const lastWeeks = segWeeks.slice(-4);
  const trendCharts = useMemo(() => {
    const out = [];
    for (const name of SLA_PREF) {
      const kpi = data.sla.kpis.find((k) => k.name === name);
      if (!kpi) continue;
      const pts = data.sla.weeks.map((w) => ({ w: wkShort(w.label), v: w.segments[segment]?.[name] })).filter((p) => p.v != null);
      if (pts.length >= 2) out.push({ name, target: kpi.target, pts });
      if (out.length >= 9) break;
    }
    return out;
  }, [data, segment]);

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {IV_METRICS(t).map((m) => {
          const v = kpis[m.k];
          const met = v == null || m.target == null ? null : m.lower ? v <= m.target : v >= m.target;
          return (
            <KpiCard key={m.k} testid={`iv-kpi-${m.k}`} label={m.label} color={colors[m.k]}
              value={m.pct ? fmtPct(v) : m.int ? fmtInt(v) : fmtN(v, m.d ?? 1)}
              badge={met != null && <Badge tone={met ? "good" : "bad"}>{met ? "met" : "miss"} · tgt {m.target}{m.pct ? "%" : ""}</Badge>}
              sub={`to ${covLabel(covMap[m.k])}`}>
              <div className="mt-3"><Spark pts={seriesFor[m.k]} color={colors[m.k]} /></div>
            </KpiCard>
          );
        })}
      </div>

      <div className={card} data-testid="iv-site-split">
        <p className="font-mono-hud mb-1 text-[10px] uppercase tracking-[0.25em] text-thdim">Qatar vs India · selected range</p>
        <p className="mb-5 text-xs text-thdim">Ignores the Site filter by design</p>
        <div className="grid gap-x-10 gap-y-5 md:grid-cols-2">
          {sitePair.map((m) => {
            const max = Math.max(m.q || 0, m.i || 0) || 1;
            return (
              <div key={m.label}>
                <p className="mb-1.5 text-xs font-medium text-thbody">{m.label}</p>
                {[["Qatar", m.q], ["India", m.i]].map(([s, v]) => (
                  <div key={s} className="mb-1 flex items-center gap-2">
                    <span className="w-11 font-mono-hud text-[9px] uppercase text-thdim">{s}</span>
                    <div className="h-3 flex-1 rounded-sm bg-thhover/10">
                      <div className="h-3 rounded-sm transition-all" style={{ width: `${v == null ? 0 : (100 * v) / max}%`, background: SITE_COLORS[s] }} />
                    </div>
                    <span className="w-14 text-right font-mono-hud text-[10px] text-thbody">{m.fmt(v)}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <div className={card} data-testid="iv-sla-scorecard">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">Weekly SLA Scorecard</p>
            <p className="mt-1 text-xs text-thdim">From the SLAs Tracker tab · not affected by the date filter</p>
          </div>
          <div className="flex gap-1 rounded-lg bg-thhover/5 p-1">
            {data.sla.segments.map((s) => (
              <button key={s} data-testid={`iv-sla-seg-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setSegment(s)}
                className={`rounded-md px-3 py-1 text-xs font-medium ${segment === s ? "bg-thcyan/15 text-thcyan" : "text-thmute hover:text-thbody"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        {lastWeeks.length === 0 ? (
          <p className="py-6 text-center text-sm text-thdim">No populated weeks for this segment yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead><tr className="border-b border-thborder/10">
                <th className="font-mono-hud px-3 py-2 text-[9px] uppercase tracking-[0.15em] text-thdim">KPI</th>
                <th className="font-mono-hud px-3 py-2 text-[9px] uppercase tracking-[0.15em] text-thdim">Target</th>
                {lastWeeks.map((w, i) => (
                  <th key={w.label} className={`font-mono-hud px-3 py-2 text-right text-[9px] uppercase tracking-[0.15em] ${i === lastWeeks.length - 1 ? "text-thcyan" : "text-thdim"}`}>{wkShort(w.label)}</th>
                ))}
                <th className="font-mono-hud px-3 py-2 text-[9px] uppercase tracking-[0.15em] text-thdim">Status</th>
              </tr></thead>
              <tbody>
                {slaKpis.map((k) => {
                  const latest = lastWeeks[lastWeeks.length - 1]?.segments[segment]?.[k.name];
                  const lower = /shrinkage|aht|abandon/i.test(k.name);
                  const met = latest == null ? null : lower ? latest <= k.target : latest >= k.target;
                  return (
                    <tr key={k.name} className="border-b border-thborder/5">
                      <td className="px-3 py-2 text-xs text-thbody">{k.name}</td>
                      <td className="px-3 py-2 font-mono-hud text-xs text-thdim">{k.pct ? fmtPct(k.target) : fmtN(k.target, 0)}</td>
                      {lastWeeks.map((w, i) => {
                        const v = w.segments[segment]?.[k.name];
                        return <td key={w.label} className={`px-3 py-2 text-right font-mono-hud text-xs ${i === lastWeeks.length - 1 ? "font-semibold text-thstrong" : "text-thbody"}`}>{v == null ? "—" : k.pct ? fmtPct(v) : fmtN(v, 0)}</td>;
                      })}
                      <td className="px-3 py-2">
                        {met == null ? <span className="text-xs text-thdim">—</span> : (
                          <span className={`rounded-full px-2 py-0.5 font-mono-hud text-[9px] uppercase ${met ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#F43F5E]/15 text-[#F43F5E]"}`}>{met ? "met" : "miss"}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div data-testid="iv-sla-trends">
        <p className="font-mono-hud mb-4 text-[10px] uppercase tracking-[0.25em] text-thdim">Weekly SLA Trends · {segment} · full history</p>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {trendCharts.map((c) => (
            <div key={c.name} className={card}>
              <p className="mb-2 truncate text-xs font-medium text-thbody">{c.name}</p>
              <div className="h-36">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100} initialDimension={{ width: 1, height: 1 }}>
                  <LineChart data={c.pts} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
                    <XAxis dataKey="w" tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} domain={["auto", "auto"]} />
                    <Tooltip contentStyle={{ background: "rgb(var(--th-solid))", border: "1px solid rgb(var(--th-border) / 0.14)", borderRadius: 10, fontSize: 11, color: "rgb(var(--th-strong))" }} labelStyle={{ color: "rgb(var(--th-mute))" }} />
                    {c.target != null && <ReferenceLine y={c.target} stroke={C.yellow} strokeDasharray="5 4" strokeOpacity={0.7} />}
                    <Line type="monotone" dataKey="v" stroke={C.cyan} strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
